import React, { useState, useMemo, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface InvestmentProject {
  id: string;
  name: string;
  description: string;
  location: string;
  total_plots: number;
  plot_price: number;
  grid_cols: number;
  grid_rows: number;
  cooperative_name: string;
  cooperative_contact: string;
  project_details: any;
}

interface PlotPurchase {
  plot_number: number;
  payment_status: string;
  buyer_email: string;
}

interface InvestmentGridProps {
  projectId?: string;
}

export function InvestmentGrid({ projectId }: InvestmentGridProps) {
  const [project, setProject] = useState<InvestmentProject | null>(null);
  const [purchases, setPurchases] = useState<PlotPurchase[]>([]);
  const [loading, setLoading] = useState(true);
  const [buyerInfo, setBuyerInfo] = useState({
    name: '',
    email: '',
    phone: ''
  });
  const [selectedPlot, setSelectedPlot] = useState<number | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [processing, setProcessing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    loadProjectData();
  }, [projectId]);

  useEffect(() => {
    // Check for payment success in URL params
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('success') === 'true') {
      const plotNumber = urlParams.get('plot');
      toast({
        title: "Betaling geslaagd!",
        description: `Plot ${plotNumber} is succesvol gekocht. Bedankt voor uw investering!`,
      });
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
      loadProjectData(); // Refresh data
    }
  }, []);

  const loadProjectData = async () => {
    try {
      setLoading(true);
      
      // Load default project if no projectId specified
      const query = projectId 
        ? supabase.from('investment_projects').select('*').eq('id', projectId)
        : supabase.from('investment_projects').select('*').eq('name', 'Snellaadparking Pannerden');
      
      const { data: projectData, error: projectError } = await query.single();
      
      if (projectError) throw projectError;
      setProject(projectData);

      // Load purchases for this project
      const { data: purchaseData, error: purchaseError } = await supabase
        .from('plot_purchases')
        .select('plot_number, payment_status, buyer_email')
        .eq('project_id', projectData.id)
        .eq('payment_status', 'completed');

      if (purchaseError) throw purchaseError;
      setPurchases(purchaseData || []);
    } catch (error) {
      console.error('Error loading project data:', error);
      toast({
        title: "Fout bij laden",
        description: "Kon projectgegevens niet laden",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const soldPlots = useMemo(() => {
    return new Set(purchases.map(p => p.plot_number));
  }, [purchases]);

  const progressPercentage = project ? Math.round((soldPlots.size / project.total_plots) * 100) : 0;
  const totalRaised = project ? soldPlots.size * project.plot_price : 0;
  const targetAmount = project ? project.total_plots * project.plot_price : 0;

  const handlePlotClick = (plotId: number) => {
    if (soldPlots.has(plotId)) return; // Al verkocht
    setSelectedPlot(plotId);
    setIsDialogOpen(true);
  };

  const handlePurchase = async () => {
    if (!project || !selectedPlot || !buyerInfo.email || !buyerInfo.name) {
      toast({
        title: "Incomplete gegevens",
        description: "Vul alle verplichte velden in",
        variant: "destructive",
      });
      return;
    }

    setProcessing(true);
    try {
      const { data, error } = await supabase.functions.invoke('create-plot-payment', {
        body: {
          project_id: project.id,
          plot_number: selectedPlot,
          buyer_email: buyerInfo.email,
          buyer_name: buyerInfo.name,
          buyer_phone: buyerInfo.phone,
        },
      });

      if (error) throw error;

      // Open Stripe checkout in new tab
      if (data.url) {
        window.open(data.url, '_blank');
        setIsDialogOpen(false);
        setBuyerInfo({ name: '', email: '', phone: '' });
        setSelectedPlot(null);
      }
    } catch (error) {
      console.error('Error creating payment:', error);
      toast({
        title: "Fout bij betaling",
        description: "Kon betaling niet initiëren. Probeer opnieuw.",
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const getPlotClassName = (plotId: number) => {
    const baseClasses = "w-full aspect-square border border-slate-600 transition-all duration-200 cursor-pointer text-xs flex items-center justify-center font-mono";
    
    if (soldPlots.has(plotId)) {
      return `${baseClasses} bg-emerald-500/80 text-slate-900 cursor-not-allowed`;
    }
    return `${baseClasses} bg-slate-700/50 hover:bg-emerald-500/30 text-slate-300`;
  };

  if (loading) {
    return <div className="rounded-3xl bg-slate-900/50 p-6 ring-1 ring-white/10">
      <div className="animate-pulse">Projectgegevens laden...</div>
    </div>;
  }

  if (!project) {
    return <div className="rounded-3xl bg-slate-900/50 p-6 ring-1 ring-white/10">
      <div className="text-red-400">Project niet gevonden</div>
    </div>;
  }

  return (
    <div className="rounded-3xl bg-slate-900/50 p-6 ring-1 ring-white/10">
      <div className="mb-6">
        <h4 className="text-xl font-semibold mb-2">Investeerdersplattegrond</h4>
        <p className="text-sm text-slate-300 mb-4">
          Koop een vierkant op de zonnecarport-plattegrond. Elke plot van €{project.plot_price.toLocaleString()} geeft recht op jaarlijkse opbrengst.
        </p>
        
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div className="bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-400">Voortgang</p>
            <p className="text-lg font-semibold">{progressPercentage}%</p>
            <div className="w-full bg-slate-700 rounded-full h-2 mt-1">
              <div 
                className="bg-emerald-500 h-2 rounded-full transition-all duration-500" 
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>
          <div className="bg-slate-800/50 rounded-xl p-3">
            <p className="text-xs text-slate-400">Opgehaald</p>
            <p className="text-lg font-semibold">€{totalRaised.toLocaleString()}</p>
            <p className="text-xs text-slate-400">van €{targetAmount.toLocaleString()}</p>
          </div>
        </div>
      </div>

      <div className="mb-4">
        <div className="grid grid-cols-2 gap-4 text-xs">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-emerald-500/80 rounded border border-slate-600"></div>
            <span className="text-slate-300">Verkocht ({soldPlots.size})</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-slate-700/50 rounded border border-slate-600"></div>
            <span className="text-slate-300">Beschikbaar ({project.total_plots - soldPlots.size})</span>
          </div>
        </div>
      </div>

      <div 
        className="grid gap-1 mb-4 mx-auto max-w-lg"
        style={{ 
          gridTemplateColumns: `repeat(${project.grid_cols}, minmax(0, 1fr))`,
          gridTemplateRows: `repeat(${project.grid_rows}, minmax(0, 1fr))`
        }}
      >
        {Array.from({ length: project.total_plots }, (_, i) => {
          const plotId = i + 1;
          return (
            <button
              key={plotId}
              className={getPlotClassName(plotId)}
              onClick={() => handlePlotClick(plotId)}
              disabled={soldPlots.has(plotId)}
              title={soldPlots.has(plotId) ? `Plot ${plotId} - Verkocht` : `Plot ${plotId} - €${project.plot_price.toLocaleString()}`}
            >
              {plotId}
            </button>
          );
        })}
      </div>

      <div className="text-center">
        {progressPercentage < 100 ? (
          <div>
            <p className="text-sm text-slate-300 mb-3">
              Nog {project.total_plots - soldPlots.size} plots beschikbaar
            </p>
          </div>
        ) : (
          <div className="bg-emerald-500/20 rounded-xl p-4 border border-emerald-500/30">
            <h5 className="font-semibold text-emerald-300 mb-1">🎉 Investeringsronde Gesloten!</h5>
            <p className="text-sm text-slate-300">
              Alle plots zijn verkocht. Het project gaat nu de realisatiefase in.
            </p>
          </div>
        )}
      </div>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="bg-slate-900 text-slate-50 border-slate-700">
          <DialogHeader>
            <DialogTitle>Plot {selectedPlot} kopen</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Naam *</Label>
              <Input
                id="name"
                value={buyerInfo.name}
                onChange={(e) => setBuyerInfo({...buyerInfo, name: e.target.value})}
                className="bg-slate-800 border-slate-600"
                placeholder="Uw volledige naam"
              />
            </div>
            <div>
              <Label htmlFor="email">E-mail *</Label>
              <Input
                id="email"
                type="email"
                value={buyerInfo.email}
                onChange={(e) => setBuyerInfo({...buyerInfo, email: e.target.value})}
                className="bg-slate-800 border-slate-600"
                placeholder="uw@email.nl"
              />
            </div>
            <div>
              <Label htmlFor="phone">Telefoon</Label>
              <Input
                id="phone"
                value={buyerInfo.phone}
                onChange={(e) => setBuyerInfo({...buyerInfo, phone: e.target.value})}
                className="bg-slate-800 border-slate-600"
                placeholder="06-12345678"
              />
            </div>
            <div className="bg-slate-800/50 rounded-lg p-4">
              <p className="text-sm text-slate-300">
                <strong>Investering:</strong> €{project.plot_price.toLocaleString()}<br/>
                <strong>Plot nummer:</strong> {selectedPlot}<br/>
                <strong>Verwacht rendement:</strong> {project.project_details?.expected_roi || 8.5}% per jaar
              </p>
            </div>
            <Button 
              onClick={handlePurchase} 
              disabled={processing || !buyerInfo.name || !buyerInfo.email}
              className="w-full bg-emerald-500 text-slate-900 hover:bg-emerald-400"
            >
              {processing ? 'Bezig...' : `Betaal €${project.plot_price.toLocaleString()}`}
            </Button>
            <p className="text-xs text-slate-400">
              U wordt doorgestuurd naar een beveiligde Stripe betalingspagina
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}